import { useEffect } from "react";

/** Merge immutable parent scope filters (project/customer) with user-selected filters. */
export function mergeScopedFilters(baseFilters = [], userFilters = []) {
  const base = baseFilters || [];
  const baseNames = new Set(base.map((f) => f.name));
  const merged = [...base];
  (userFilters || []).forEach((filter) => {
    if (!baseNames.has(filter.name)) {
      merged.push(filter);
    }
  });
  return merged;
}

/** Stable key for scope filter arrays (ignores reference changes). */
export function scopeFiltersKey(filters = []) {
  return JSON.stringify(
    (filters || [])
      .map((f) => [String(f.name), f.value])
      .sort(([a], [b]) => a.localeCompare(b))
  );
}

export function filtersEqual(a = [], b = []) {
  return scopeFiltersKey(a) === scopeFiltersKey(b);
}

export function nonScopedFilters(scopeFilters = [], filters = []) {
  const scoped = new Set((scopeFilters || []).map((x) => x.name));
  return (filters || []).filter((f) => !scoped.has(f.name));
}

export function syncScopedControllerFilters(scopeFilters, filters = []) {
  return mergeScopedFilters(
    scopeFilters,
    nonScopedFilters(scopeFilters, filters)
  );
}

/**
 * Sync parent scope filters into list controller state.
 * Depends on serialized filter values, not array reference.
 */
export function useScopeFiltersSync(scopeFilters, setController) {
  const scopeKey = scopeFiltersKey(scopeFilters);

  useEffect(() => {
    setController((c) => {
      const nextFilters = syncScopedControllerFilters(scopeFilters, c.filters);
      if (filtersEqual(c.filters, nextFilters)) return c;
      return { ...c, page: 1, filters: nextFilters };
    });
    // scopeFilters read from render when scopeKey changes
  }, [scopeKey, setController]); // eslint-disable-line react-hooks/exhaustive-deps
}

/** Build tableKey suffix so embedded lists don't clash with top-level list prefs. */
export function embeddedTableKey(moduleName, scopeKey = "embedded") {
  const mod = moduleName || "module";
  const scope = scopeKey || "embedded";
  return `${mod}_${scope}`;
}

/** Safe hasMore for infinite scroll (avoids load-more while loading or when empty). */
export function computeHasMore({ isLoading, dataLength, totalCount }) {
  const loaded = Number(dataLength) || 0;
  const total = Number(totalCount) || 0;
  return !isLoading && loaded > 0 && loaded < total;
}

/** Stable fetch key for embedded list controllers (excludes page / append). */
export function embeddedFetchKey(controller = {}, extra = {}) {
  return JSON.stringify({
    filters: scopeFiltersKey(controller.filters),
    query: controller.query ?? "",
    sort: controller.sort,
    sortBy: controller.sortBy,
    per_page: controller.per_page,
    view: controller.view,
    fields: controller.fields,
    ...extra,
  });
}
